import {
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  Lock,
  Mail,
  MessageCircle,
  Pencil,
  QrCode,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "@/lib/browserRouter";
import { usePurchaseStatus } from "@/hooks/usePurchaseStatus";
import {
  getCommerceCheckout,
  getStorefrontTheme,
  initiateCommercePayment,
  lookupCep,
  prepareCommerceCheckout,
  saveCommerceCheckoutCustomer,
  tokenizeCommerceCard,
} from "@/lib/commerceCheckout";
import { pickLbApiBase } from "@/lib/pickLbApi";
import { executeCommerceRecaptcha } from "@/lib/recaptchaEnterprise";
import {
  formatCreditCardNumber,
  formatExpDate,
  formatPhoneNumber,
  formatTaxId,
  getInstallments,
  isValidBrazilianPhone,
  isValidCNPJ,
  isValidCPF,
  isValidCreditCardNumber,
  isValidEmail,
  isValidName,
} from "../Checkout/helpers";
import "./CommerceCheckout.css";

const storefrontBaseUrl = String(
  (import.meta.env.DEV
    ? import.meta.env.VITE_DEV_STOREFRONT_URL
    : import.meta.env.VITE_STABLE_STOREFRONT_URL) ||
    import.meta.env.VITE_STOREFRONT_URL ||
    "http://localhost:5173"
).replace(/\/$/, "");

const emptyCustomer = { email: "", name: "", taxId: "", phone: "" };
const emptyAddress = {
  zipCode: "",
  street: "",
  number: "",
  noNumber: false,
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
};
const emptyCard = { holderName: "", number: "", expDate: "", cvv: "" };

function draftKey(slug) {
  return `ap.commerce.checkout.draft.${slug || "store"}`;
}

function readDraft(slug) {
  try {
    const raw = sessionStorage.getItem(draftKey(slug));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeDraft(slug, payload) {
  try {
    sessionStorage.setItem(draftKey(slug), JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

function clearDraft(slug) {
  try {
    sessionStorage.removeItem(draftKey(slug));
  } catch {
    /* ignore */
  }
}

function formatMoney(amountMinor, currency = "BRL") {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
  }).format(Number(amountMinor) / 100);
}

function formatCep(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

function validTaxId(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length <= 11 ? isValidCPF(digits) : isValidCNPJ(digits);
}

function isValidExpDate(value) {
  const [monthRaw, yearRaw] = String(value || "").split("/");
  const month = Number(monthRaw);
  const year = Number(yearRaw);
  if (!month || month < 1 || month > 12 || !year || yearRaw?.length !== 2) {
    return false;
  }
  const expiry = new Date(2000 + year, month, 0, 23, 59, 59, 999);
  return expiry.getTime() >= Date.now();
}

function applyStoreFavicon(url) {
  document.head.querySelector('link[data-store-favicon="true"]')?.remove();
  if (!url) return;
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = url;
  link.dataset.storeFavicon = "true";
  document.head.appendChild(link);
}

function applyThemeColor(color) {
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = color || "#ffffff";
}

function storefrontUrl(order, suffix = "") {
  const path = suffix.startsWith("/") ? suffix : suffix ? `/${suffix}` : "";
  if (order?.store?.primaryHostname) {
    return `https://${order.store.primaryHostname}${path || "/"}`;
  }
  return `${storefrontBaseUrl}/commerce/${encodeURIComponent(
    order.store.slug
  )}${path}`;
}

function checkoutPath(token) {
  return `/commerce/${encodeURIComponent(token)}`;
}

function whatsappHref(raw) {
  let digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10 || digits.length === 11) digits = `55${digits}`;
  return `https://wa.me/${digits}`;
}

function statementHint(storeName) {
  return String(storeName || "ATOMICPAY")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9 ]/g, "")
    .trim()
    .slice(0, 13)
    .toUpperCase();
}

function identityErrors(customer) {
  const next = {};
  if (!isValidEmail(customer.email)) next.email = "Informe um e-mail válido.";
  if (!isValidName(customer.name)) next.name = "Informe seu nome completo.";
  if (!validTaxId(customer.taxId)) next.taxId = "Informe um CPF ou CNPJ válido.";
  if (!isValidBrazilianPhone(customer.phone)) {
    next.phone = "Informe um telefone com DDD.";
  }
  return next;
}

function addressErrors(address) {
  const next = {};
  if (address.zipCode.replace(/\D/g, "").length !== 8) {
    next.zipCode = "Informe um CEP válido.";
  }
  if (!address.street.trim()) next.street = "Informe o logradouro.";
  if (!address.noNumber && !String(address.number).trim()) {
    next.number = "Informe o número ou marque S/N.";
  }
  if (!address.neighborhood.trim()) next.neighborhood = "Informe o bairro.";
  if (!address.city.trim()) next.city = "Informe a cidade.";
  if (!/^[A-Za-z]{2}$/.test(address.state.trim())) next.state = "Informe a UF.";
  return next;
}

function paymentErrors(paymentMethod, card, installments, installmentOptions) {
  const next = {};
  if (paymentMethod !== "credit_card") return next;
  if (!card.holderName.trim() || card.holderName.trim().length < 3) {
    next.holderName = "Informe o nome impresso no cartão.";
  }
  if (!isValidCreditCardNumber(card.number)) {
    next.number = "Informe um número de cartão válido.";
  }
  if (!isValidExpDate(card.expDate)) {
    next.expDate = "Informe uma validade válida.";
  }
  if (!/^\d{3,4}$/.test(card.cvv)) next.cvv = "Informe o CVV.";
  if (!installmentOptions.some((option) => option.qtt === installments)) {
    next.installments = "Escolha o parcelamento.";
  }
  return next;
}

function formatAddressLine(address) {
  const number = address.noNumber ? "S/N" : address.number;
  const street = [address.street, number].filter(Boolean).join(", ");
  const city = [address.city, address.state].filter(Boolean).join(" - ");
  const cep = address.zipCode ? `CEP ${formatCep(address.zipCode)}` : "";
  return [street, address.neighborhood, city, cep].filter(Boolean).join(" · ");
}

function CheckoutLoading() {
  return (
    <main className="commerce-checkout-state">
      <span className="commerce-spinner" />
      <p>Carregando checkout seguro...</p>
    </main>
  );
}

function PixCountdown({ expirationTime }) {
  const [timeLeft, setTimeLeft] = useState(
    Math.max(0, expirationTime - Date.now())
  );
  useEffect(() => {
    const interval = window.setInterval(
      () => setTimeLeft(Math.max(0, expirationTime - Date.now())),
      1000
    );
    return () => window.clearInterval(interval);
  }, [expirationTime]);
  const seconds = Math.floor(timeLeft / 1000);
  const minutes = Math.floor(seconds / 60);
  return (
    <span>
      {String(minutes).padStart(2, "0")}:
      {String(seconds % 60).padStart(2, "0")}
    </span>
  );
}

function Field({
  label,
  error,
  hint,
  className = "",
  children,
}) {
  return (
    <label className={`commerce-field ${className}`.trim()}>
      <span>{label}</span>
      {children}
      {hint ? <em>{hint}</em> : null}
      {error ? <small>{error}</small> : null}
    </label>
  );
}

export default function CommerceCheckout() {
  const { checkoutToken } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [expired, setExpired] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("credit_card");
  const [installments, setInstallments] = useState(1);
  const [customer, setCustomer] = useState(emptyCustomer);
  const [address, setAddress] = useState(emptyAddress);
  const [cepStatus, setCepStatus] = useState("");
  const [card, setCard] = useState(emptyCard);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [purchaseId, setPurchaseId] = useState("");
  const [copied, setCopied] = useState(false);
  const lastResolvedCep = useRef("");
  const lbApiUrl = String(pickLbApiBase() || "").replace(/\/$/, "");
  const { purchaseData, error: pollError } = usePurchaseStatus(
    purchaseId,
    lbApiUrl
  );

  const installmentOptions = useMemo(
    () => getInstallments(order?.totalMinor || 0),
    [order?.totalMinor]
  );
  const selectedInstallment =
    installmentOptions.find((option) => option.qtt === installments) ||
    installmentOptions[installmentOptions.length - 1];
  const payAmountMinor = selectedInstallment
    ? Math.round(Number(selectedInstallment.total) * 100)
    : order?.totalMinor || 0;

  const applyOrder = (result) => {
    setOrder(result);
    document.title = `Checkout | ${result.store.name}`;
    applyStoreFavicon(result.store?.faviconUrl);
    const draft = readDraft(result.store.slug);
    const savedCustomer = draft?.customer || result.customer;
    if (savedCustomer) {
      setCustomer({ ...emptyCustomer, ...savedCustomer });
    }
    const savedAddress = draft?.address || result.customer?.address;
    if (savedAddress) {
      setAddress({
        ...emptyAddress,
        ...savedAddress,
        zipCode: formatCep(savedAddress.zipCode || savedAddress.zip_code || ""),
        street: savedAddress.street || savedAddress.line1 || "",
        complement: savedAddress.complement || savedAddress.line2 || "",
      });
      lastResolvedCep.current = String(
        savedAddress.zipCode || savedAddress.zip_code || ""
      ).replace(/\D/g, "");
    }
    if (draft?.step >= 1 && draft.step <= 3) setStep(draft.step);
    if (draft?.paymentMethod) setPaymentMethod(draft.paymentMethod);
    if (draft?.installments) setInstallments(Number(draft.installments) || 1);
    if (draft?.cardHolder) {
      setCard((current) => ({ ...current, holderName: draft.cardHolder }));
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setExpired(false);
    getCommerceCheckout(checkoutToken, { signal: controller.signal })
      .then(async (result) => {
        applyOrder(result);
        const nextTheme =
          result.theme ||
          (await getStorefrontTheme(result.store?.slug, {
            signal: controller.signal,
          }).catch(() => null));
        setTheme(nextTheme);
        applyThemeColor(
          nextTheme?.primaryColor || nextTheme?.backgroundColor || "#ffffff"
        );
      })
      .catch(async (error) => {
        if (error.name === "AbortError") return;
        if (error.payload?.store) {
          applyOrder(error.payload);
          setExpired(true);
          const nextTheme =
            error.payload.theme ||
            (await getStorefrontTheme(error.payload.store?.slug, {
              signal: controller.signal,
            }).catch(() => null));
          setTheme(nextTheme);
          return;
        }
        setLoadError(error.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      controller.abort();
      applyStoreFavicon("");
    };
  }, [checkoutToken]);

  useEffect(() => {
    if (!order?.store?.slug) return;
    writeDraft(order.store.slug, {
      customer,
      address,
      step,
      paymentMethod,
      installments,
      cardHolder: card.holderName,
    });
  }, [order?.store?.slug, customer, address, step, paymentMethod, installments, card.holderName]);

  useEffect(() => {
    const digits = address.zipCode.replace(/\D/g, "");
    if (digits.length !== 8) {
      setCepStatus("");
      return undefined;
    }
    if (lastResolvedCep.current === digits && address.street && address.city) {
      return undefined;
    }
    const controller = new AbortController();
    setCepStatus("Buscando endereço...");
    lookupCep(digits, { signal: controller.signal })
      .then((result) => {
        lastResolvedCep.current = digits;
        setAddress((current) => ({
          ...current,
          street: result.street || current.street,
          neighborhood: result.neighborhood || current.neighborhood,
          city: result.city || "",
          state: result.state || "",
          complement: current.complement || result.complement || "",
        }));
        setCepStatus("");
        setErrors((current) => ({ ...current, zipCode: undefined }));
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setCepStatus("");
        setErrors((current) => ({
          ...current,
          zipCode: error.message || "CEP não encontrado.",
        }));
      });
    return () => controller.abort();
  }, [address.zipCode]);

  useEffect(() => {
    if (!purchaseData) return;
    if (purchaseData.status === "approved") {
      clearDraft(order?.store?.slug);
      window.location.assign(
        `${storefrontUrl(order, "/order-confirmed")}?orderId=${encodeURIComponent(order.id)}`
      );
      return;
    }
    if (["declined", "failed"].includes(purchaseData.status)) {
      setSubmitting(false);
      setErrors({
        payment:
          purchaseData.status_description ||
          "O pagamento não foi aprovado. Tente novamente.",
      });
    }
  }, [order, purchaseData]);

  useEffect(() => {
    if (pollError) {
      setSubmitting(false);
      setErrors({ payment: pollError.message || "Falha ao consultar pagamento." });
    }
  }, [pollError]);

  const setFieldError = (name, value) => {
    setErrors((current) => ({ ...current, [name]: value }));
  };

  const renewCheckout = async () => {
    if (!order?.items?.length) return;
    setRenewing(true);
    setErrors((current) => ({ ...current, payment: undefined }));
    try {
      const next = await prepareCommerceCheckout(order.store.slug, order.items);
      setExpired(false);
      navigate(checkoutPath(next.checkoutToken), { replace: true });
    } catch (error) {
      setErrors({
        payment:
          error.message ||
          "Não foi possível renovar o pedido. Verifique o estoque e tente novamente.",
      });
    } finally {
      setRenewing(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (step === 1) {
      const next = identityErrors(customer);
      setErrors(next);
      if (!Object.keys(next).length) {
        setStep(2);
        saveCommerceCheckoutCustomer(checkoutToken, {
          email: customer.email,
          name: customer.name,
          taxId: customer.taxId,
          phone: customer.phone,
        }).catch(() => {});
      }
      return;
    }
    if (step === 2) {
      const next = addressErrors(address);
      setErrors(next);
      if (!Object.keys(next).length) {
        setStep(3);
        saveCommerceCheckoutCustomer(checkoutToken, {
          email: customer.email,
          name: customer.name,
          taxId: customer.taxId,
          phone: customer.phone,
          address: {
            street: address.street.trim(),
            number: address.noNumber ? "S/N" : address.number.trim(),
            complement: address.complement.trim(),
            neighborhood: address.neighborhood.trim(),
            city: address.city.trim(),
            state: address.state.trim().toUpperCase(),
            zipCode: address.zipCode.replace(/\D/g, ""),
            country: "BR",
          },
        }).catch(() => {});
      }
      return;
    }
    const next = paymentErrors(
      paymentMethod,
      card,
      installments,
      installmentOptions
    );
    setErrors(next);
    if (Object.keys(next).length) return;
    if (expired) {
      setExpired(true);
      return;
    }
    setSubmitting(true);
    setErrors({});
    try {
      const recaptchaToken = await executeCommerceRecaptcha();
      const cardToken =
        paymentMethod === "credit_card"
          ? await tokenizeCommerceCard(card)
          : null;
      const result = await initiateCommercePayment({
        checkoutToken,
        customer: {
          email: customer.email.trim().toLowerCase(),
          name: customer.name.trim(),
          taxId: customer.taxId,
          phoneNumber: customer.phone,
          address: {
            street: address.street.trim(),
            number: address.noNumber ? "S/N" : address.number.trim(),
            complement: address.complement.trim(),
            neighborhood: address.neighborhood.trim(),
            city: address.city.trim(),
            state: address.state.trim().toUpperCase(),
            zipCode: address.zipCode.replace(/\D/g, ""),
            country: "BR",
          },
        },
        purchase: {
          paymentMethod,
          installments: paymentMethod === "credit_card" ? installments : 1,
          creditCard: cardToken ? { token: cardToken } : undefined,
          recaptchaToken,
        },
      });
      const nextPurchaseId =
        result.purchaseId || result.id || result.purchase?.id;
      if (!nextPurchaseId) {
        throw new Error("O pagamento não retornou um identificador.");
      }
      setPurchaseId(nextPurchaseId);
      if (result.status === "declined" || result.status === "failed") {
        throw new Error(result.message || "Pagamento não aprovado.");
      }
    } catch (error) {
      setSubmitting(false);
      if (
        error.code === "order_not_payable" ||
        error.code === "checkout_expired"
      ) {
        setExpired(true);
        return;
      }
      setErrors({ payment: error.message });
    }
  };

  const pix =
    purchaseData?.status === "pix_generated" ? purchaseData.pix : null;
  const pixExpiration =
    Number(purchaseData?.createdAt || Date.now()) + 30 * 60 * 1000;
  const supportEmail = order?.store?.supportEmail || "";
  const whatsappUrl = whatsappHref(order?.store?.whatsappNumber);

  if (loading) return <CheckoutLoading />;
  if (loadError || !order) {
    return (
      <main className="commerce-checkout-state">
        <h1>Checkout indisponível</h1>
        <p>{loadError || "Não foi possível localizar este pedido."}</p>
      </main>
    );
  }

  return (
    <main
      className="commerce-checkout-page"
      data-store-theme={theme?.themeId || undefined}
      style={
        theme
          ? {
              "--commerce-accent": theme.primaryColor,
              "--commerce-accent-dark": theme.primaryColor,
              "--commerce-text": theme.textColor,
              "--commerce-muted": theme.mutedColor,
              "--commerce-page-bg": theme.backgroundColor,
              "--commerce-button-bg":
                theme.primaryGradientStart && theme.primaryGradientEnd
                  ? `linear-gradient(135deg, ${theme.primaryGradientStart}, ${theme.primaryGradientEnd})`
                  : theme.buttonColor,
              "--commerce-button-text": theme.buttonTextColor,
              "--commerce-surface": theme.themeDark ? "#111111" : "#ffffff",
              "--commerce-border": theme.themeDark ? "#27272a" : "#e4e7ec",
            }
          : undefined
      }
    >
      <header className="commerce-checkout-header">
        <a href={storefrontUrl(order)}>
          {order.store.logoUrl ? (
            <img src={order.store.logoUrl} alt="" />
          ) : (
            <span>{order.store.name.slice(0, 1).toUpperCase()}</span>
          )}
          <div>
            <small>Checkout seguro</small>
            <strong>{order.store.name}</strong>
          </div>
        </a>
        <div className="commerce-checkout-header-meta">
          {supportEmail ? (
            <a href={`mailto:${supportEmail}`}>
              <Mail />
              {supportEmail}
            </a>
          ) : null}
          <span>
            <ShieldCheck />
            Ambiente protegido
          </span>
        </div>
      </header>

      <div className="commerce-checkout-layout">
        <div className="commerce-left-stack">
          {step > 1 && (
            <article className="commerce-done-card">
              <header>
                <span className="commerce-step-badge done">
                  <Check />
                </span>
                <div>
                  <p>1. Identificação</p>
                  <strong>{customer.name}</strong>
                  <small>{customer.email}</small>
                </div>
                <button type="button" onClick={() => setStep(1)} aria-label="Editar identificação">
                  <Pencil />
                </button>
              </header>
            </article>
          )}
          {step > 2 && (
            <article className="commerce-done-card">
              <header>
                <span className="commerce-step-badge done">
                  <Check />
                </span>
                <div>
                  <p>2. Entrega</p>
                  <strong>A cargo da loja</strong>
                  <small>{formatAddressLine(address)}</small>
                </div>
                <button type="button" onClick={() => setStep(2)} aria-label="Editar entrega">
                  <Pencil />
                </button>
              </header>
            </article>
          )}

          <aside className="commerce-order-summary">
            <p className="commerce-eyebrow">Resumo da compra</p>
            <h1>Seu pedido</h1>
            <div className="commerce-order-items">
              {order.items.map((item) => (
                <article key={item.id}>
                  <div className="commerce-order-image">
                    {item.image ? (
                      <img src={item.image} alt="" />
                    ) : (
                      <span>{item.title.slice(0, 1)}</span>
                    )}
                    <b>{item.quantity}</b>
                  </div>
                  <div>
                    <strong>{item.title}</strong>
                    <small>
                      {Object.values(item.options || {}).join(" / ") ||
                        item.variantTitle}
                    </small>
                  </div>
                  <p>{formatMoney(item.lineAmountMinor, item.currency)}</p>
                </article>
              ))}
            </div>
            <div className="commerce-order-totals">
              <div>
                <span>Subtotal</span>
                <strong>{formatMoney(order.subtotalMinor, order.currency)}</strong>
              </div>
              <div>
                <span>Frete</span>
                <strong>A cargo da loja</strong>
              </div>
              <div className="commerce-order-total">
                <span>Total</span>
                <strong>
                  {paymentMethod === "credit_card"
                    ? formatMoney(payAmountMinor, order.currency)
                    : formatMoney(order.totalMinor, order.currency)}
                </strong>
              </div>
            </div>
          </aside>
        </div>

        <section className="commerce-payment-panel">
          {pix ? (
            <div className="commerce-pix-result">
              <div className="commerce-pix-icon">
                <QrCode />
              </div>
              <p className="commerce-eyebrow">PIX gerado</p>
              <h2>Escaneie para pagar</h2>
              <p>
                Após o pagamento, esta página será atualizada automaticamente.
              </p>
              {pix.url && <img src={pix.url} alt="QR Code PIX" />}
              <button
                type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(pix.qrcode);
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1600);
                }}
              >
                {copied ? <CheckCircle2 /> : <Copy />}
                {copied ? "Código copiado" : "Copiar código PIX"}
              </button>
              <small>
                Expira em <PixCountdown expirationTime={pixExpiration} />
              </small>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              <ol className="commerce-steps">
                {[
                  { id: 1, label: "Identificação" },
                  { id: 2, label: "Entrega" },
                  { id: 3, label: "Pagamento" },
                ].map((item) => (
                  <li
                    key={item.id}
                    className={
                      step === item.id
                        ? "active"
                        : step > item.id
                          ? "complete"
                          : ""
                    }
                  >
                    <span>
                      {step > item.id ? <Check /> : item.id}
                    </span>
                    {item.label}
                  </li>
                ))}
              </ol>

              {step === 1 && (
                <>
                  <div className="commerce-form-heading">
                    <p className="commerce-eyebrow">Passo 1</p>
                    <h2>Identificação</h2>
                  </div>
                  <div className="commerce-field-grid">
                    <Field
                      label="E-mail"
                      error={errors.email}
                      className="commerce-span-2"
                    >
                      <input
                        type="email"
                        autoComplete="email"
                        value={customer.email}
                        onChange={(event) =>
                          setCustomer((current) => ({
                            ...current,
                            email: event.target.value,
                          }))
                        }
                        onBlur={() =>
                          setFieldError("email", identityErrors(customer).email)
                        }
                      />
                    </Field>
                    <Field
                      label="Nome completo"
                      error={errors.name}
                      className="commerce-span-2"
                    >
                      <input
                        autoComplete="name"
                        value={customer.name}
                        onChange={(event) =>
                          setCustomer((current) => ({
                            ...current,
                            name: event.target.value,
                          }))
                        }
                        onBlur={() =>
                          setFieldError("name", identityErrors(customer).name)
                        }
                      />
                    </Field>
                    <Field label="CPF ou CNPJ" error={errors.taxId}>
                      <input
                        inputMode="numeric"
                        value={customer.taxId}
                        onChange={(event) =>
                          setCustomer((current) => ({
                            ...current,
                            taxId: formatTaxId(event.target.value).slice(0, 18),
                          }))
                        }
                        onBlur={() =>
                          setFieldError("taxId", identityErrors(customer).taxId)
                        }
                      />
                    </Field>
                    <Field label="Telefone" error={errors.phone}>
                      <input
                        type="tel"
                        autoComplete="tel"
                        value={customer.phone}
                        onChange={(event) =>
                          setCustomer((current) => ({
                            ...current,
                            phone: formatPhoneNumber(event.target.value).slice(0, 15),
                          }))
                        }
                        onBlur={() =>
                          setFieldError("phone", identityErrors(customer).phone)
                        }
                      />
                    </Field>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="commerce-form-heading">
                    <p className="commerce-eyebrow">Passo 2</p>
                    <h2>Entrega</h2>
                  </div>
                  <p className="commerce-shipping-note">
                    Frete a cargo da loja. Entregamos em todo o Brasil.
                  </p>
                  <div className="commerce-address-grid">
                    <Field label="CEP" error={errors.zipCode} hint={cepStatus}>
                      <input
                        inputMode="numeric"
                        autoComplete="postal-code"
                        value={address.zipCode}
                        onChange={(event) => {
                          lastResolvedCep.current = "";
                          setAddress((current) => ({
                            ...current,
                            zipCode: formatCep(event.target.value),
                          }));
                        }}
                        onBlur={() =>
                          setFieldError("zipCode", addressErrors(address).zipCode)
                        }
                      />
                    </Field>
                    <Field
                      label="Endereço"
                      error={errors.street}
                      className="commerce-address-street"
                    >
                      <input
                        autoComplete="address-line1"
                        value={address.street}
                        onChange={(event) =>
                          setAddress((current) => ({
                            ...current,
                            street: event.target.value,
                          }))
                        }
                        onBlur={() =>
                          setFieldError("street", addressErrors(address).street)
                        }
                      />
                    </Field>
                    <Field label="Número" error={errors.number}>
                      <input
                        disabled={address.noNumber}
                        autoComplete="address-line2"
                        value={address.noNumber ? "S/N" : address.number}
                        onChange={(event) =>
                          setAddress((current) => ({
                            ...current,
                            number: event.target.value,
                          }))
                        }
                        onBlur={() =>
                          setFieldError("number", addressErrors(address).number)
                        }
                      />
                    </Field>
                    <label className="commerce-sn-toggle">
                      <input
                        type="checkbox"
                        checked={address.noNumber}
                        onChange={(event) =>
                          setAddress((current) => ({
                            ...current,
                            noNumber: event.target.checked,
                            number: event.target.checked ? "" : current.number,
                          }))
                        }
                      />
                      S/N
                    </label>
                    <Field label="Complemento">
                      <input
                        value={address.complement}
                        onChange={(event) =>
                          setAddress((current) => ({
                            ...current,
                            complement: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field label="Bairro" error={errors.neighborhood}>
                      <input
                        value={address.neighborhood}
                        onChange={(event) =>
                          setAddress((current) => ({
                            ...current,
                            neighborhood: event.target.value,
                          }))
                        }
                        onBlur={() =>
                          setFieldError(
                            "neighborhood",
                            addressErrors(address).neighborhood
                          )
                        }
                      />
                    </Field>
                    <Field label="Cidade" error={errors.city}>
                      <input
                        autoComplete="address-level2"
                        value={address.city}
                        onChange={(event) =>
                          setAddress((current) => ({
                            ...current,
                            city: event.target.value,
                          }))
                        }
                        onBlur={() =>
                          setFieldError("city", addressErrors(address).city)
                        }
                      />
                    </Field>
                    <Field label="UF" error={errors.state}>
                      <input
                        autoComplete="address-level1"
                        maxLength={2}
                        value={address.state}
                        onChange={(event) =>
                          setAddress((current) => ({
                            ...current,
                            state: event.target.value
                              .replace(/[^A-Za-z]/g, "")
                              .slice(0, 2)
                              .toUpperCase(),
                          }))
                        }
                        onBlur={() =>
                          setFieldError("state", addressErrors(address).state)
                        }
                      />
                    </Field>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div className="commerce-form-heading">
                    <p className="commerce-eyebrow">Passo 3</p>
                    <h2>Pagamento</h2>
                  </div>
                  <div className="commerce-payment-tabs">
                    <button
                      type="button"
                      className={paymentMethod === "credit_card" ? "active" : ""}
                      onClick={() => setPaymentMethod("credit_card")}
                    >
                      <CreditCard /> Cartão
                    </button>
                    <button
                      type="button"
                      className={paymentMethod === "pix" ? "active" : ""}
                      onClick={() => setPaymentMethod("pix")}
                    >
                      <QrCode /> PIX
                    </button>
                  </div>

                  {paymentMethod === "credit_card" && (
                    <div className="commerce-card-fields">
                      <Field label="Parcelamento" error={errors.installments}>
                        <select
                          value={installments}
                          onChange={(event) =>
                            setInstallments(Number(event.target.value))
                          }
                        >
                          {installmentOptions.map((option) => (
                            <option key={option.qtt} value={option.qtt}>
                              {option.qtt === 1
                                ? `1x de ${formatMoney(order.totalMinor, order.currency)} sem juros`
                                : `${option.qtt}x de ${formatMoney(
                                    Math.round(Number(option.value) * 100),
                                    order.currency
                                  )} (total ${formatMoney(
                                    Math.round(Number(option.total) * 100),
                                    order.currency
                                  )})`}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Nome no cartão" error={errors.holderName}>
                        <input
                          autoComplete="cc-name"
                          value={card.holderName}
                          onChange={(event) =>
                            setCard((current) => ({
                              ...current,
                              holderName: event.target.value,
                            }))
                          }
                          onBlur={() =>
                            setFieldError(
                              "holderName",
                              paymentErrors(
                                paymentMethod,
                                card,
                                installments,
                                installmentOptions
                              ).holderName
                            )
                          }
                        />
                      </Field>
                      <Field label="Número do cartão" error={errors.number}>
                        <input
                          inputMode="numeric"
                          autoComplete="cc-number"
                          value={card.number}
                          onChange={(event) =>
                            setCard((current) => ({
                              ...current,
                              number: formatCreditCardNumber(
                                event.target.value
                              ).slice(0, 19),
                            }))
                          }
                          onBlur={() =>
                            setFieldError(
                              "number",
                              paymentErrors(
                                paymentMethod,
                                card,
                                installments,
                                installmentOptions
                              ).number
                            )
                          }
                        />
                      </Field>
                      <div className="commerce-field-row">
                        <Field label="Validade" error={errors.expDate}>
                          <input
                            inputMode="numeric"
                            autoComplete="cc-exp"
                            placeholder="MM/AA"
                            value={card.expDate}
                            onChange={(event) =>
                              setCard((current) => ({
                                ...current,
                                expDate: formatExpDate(event.target.value).slice(
                                  0,
                                  5
                                ),
                              }))
                            }
                            onBlur={() =>
                              setFieldError(
                                "expDate",
                                paymentErrors(
                                  paymentMethod,
                                  card,
                                  installments,
                                  installmentOptions
                                ).expDate
                              )
                            }
                          />
                        </Field>
                        <Field label="CVV" error={errors.cvv}>
                          <input
                            type="password"
                            inputMode="numeric"
                            autoComplete="cc-csc"
                            maxLength={4}
                            value={card.cvv}
                            onChange={(event) =>
                              setCard((current) => ({
                                ...current,
                                cvv: event.target.value.replace(/\D/g, ""),
                              }))
                            }
                            onBlur={() =>
                              setFieldError(
                                "cvv",
                                paymentErrors(
                                  paymentMethod,
                                  card,
                                  installments,
                                  installmentOptions
                                ).cvv
                              )
                            }
                          />
                        </Field>
                      </div>
                      <p className="commerce-descriptor-hint">
                        A cobrança pode aparecer na fatura como{" "}
                        <strong>{statementHint(order.store.name)}</strong>.
                      </p>
                    </div>
                  )}
                </>
              )}

              {errors.payment && (
                <div className="commerce-payment-error" role="alert">
                  {errors.payment}
                </div>
              )}

              <div className="commerce-step-actions">
                {step > 1 && (
                  <button
                    type="button"
                    className="commerce-back-button"
                    onClick={() => setStep((current) => current - 1)}
                  >
                    Voltar
                  </button>
                )}
                <button
                  type="submit"
                  className="commerce-pay-button"
                  disabled={submitting || renewing}
                >
                  {submitting ? (
                    <span className="commerce-spinner" />
                  ) : step < 3 ? (
                    <ArrowRight />
                  ) : (
                    <Lock />
                  )}
                  {step < 3
                    ? "Continuar"
                    : paymentMethod === "pix"
                      ? "Gerar PIX"
                      : `Pagar ${formatMoney(payAmountMinor, order.currency)}`}
                </button>
              </div>
              {step === 3 && (
                <>
                  <p className="commerce-disclaimer">
                    Ao clicar em pagar, eu declaro que (i) estou ciente que o
                    AtomicPay está processando essa compra em nome de{" "}
                    {order.store.name} e que não possui responsabilidade pelo
                    conteúdo, oferta, e nem faz controle prévio do produto;
                    (ii) que li e concordo com os Termos de Compra, Termos de
                    Uso, e Política de Privacidade.
                  </p>
                  <p className="commerce-security-note">
                    Este site está protegido pelo Google reCAPTCHA.
                  </p>
                  <p className="commerce-policy-links">
                    <a href="https://policies.google.com/privacy">
                      Política de Privacidade
                    </a>
                    <a href="https://policies.google.com/terms">
                      Termos de Serviço
                    </a>
                  </p>
                </>
              )}
            </form>
          )}
        </section>
      </div>

      {expired ? (
        <div className="commerce-expired-overlay" role="dialog" aria-modal="true">
          <div className="commerce-expired-modal">
            <h2>Atenção</h2>
            <p>
              Página expirada. Atualize para reservar os itens novamente e
              continuar com os dados já preenchidos.
            </p>
            {errors.payment ? <small>{errors.payment}</small> : null}
            <button type="button" onClick={renewCheckout} disabled={renewing}>
              {renewing ? <span className="commerce-spinner" /> : null}
              Atualizar
            </button>
          </div>
        </div>
      ) : null}

      {whatsappUrl ? (
        <a
          className="commerce-whatsapp-fab"
          href={whatsappUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="WhatsApp"
        >
          <MessageCircle />
        </a>
      ) : null}
    </main>
  );
}
